import { useState, useEffect } from 'react';
import { PriceSlider } from './filters/price_slider';
import { BedsFilter } from './filters/beds_filter';
import { BathsFilter } from './filters/baths_filter';
import { AmenitiesFilter } from './filters/amenities_filter';
import { StylesFilter } from './filters/styles_filter';
import { UnitsFeatured } from './units_featured';
import { UnitsList } from './units_list';
import { distance, average, distanceDown } from './utils';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

let amenities = [];

let min = 99999999;
let max = 0;

let minDefault = min;
let maxDefault = max;

const numberOfFeatured = 4;
const numberOfOthers = 9999;

const defaults = {
  "beds" : 1,
  "baths" : 1,
};

let availableStyles = []; //window.styles;

if(window.units) {

  units.forEach(( unit ) => {
    // randomize the listing for debugging
    unit.dateShown = unit.availableDate;
    if(new Date(unit.availableDate) <= new Date()) unit.dateShown = 'Immediate Availability';

    // make max / min slider
    if(unit.minimumRent < min) {
      min = Math.floor(unit.minimumRent);
    }
    if(unit.maximumRent > max) {
      max = Math.ceil(unit.maximumRent);
    }

    // set default price based on beds / baths selection
    if(unit.beds == defaults.beds && unit.baths == defaults.baths) {
      if(unit.minimumRent < minDefault) {
        minDefault = unit.minimumRent;
      }
      if(unit.maximumRent > maxDefault) {
        maxDefault = unit.maximumRent;
      }
    }

    // join up style codes
    unit.project = window.projects.find(( rental ) => rental?.style_code?.toLowerCase() == unit?.style_code?.toLowerCase());

    // Fix images
    if(!unit.project && unit.style_code) {
      console.log('Cannot match style code ' + unit.style_code, unit);
    } else {
      // console.log(unit.project);
      const style = unit.project.fields.card_overrides.style;
      if (!availableStyles.includes(style) && style) {
        availableStyles.push(style); // This will not execute as 'banana' is already present
      }
    }

    // make unique array of amenities
    amenities = [...new Set([...amenities, ...unit.amenities])];
  });
}

export function Filters() {
  const [beds, setBeds] = useState(defaults.beds);
  const [baths, setBaths] = useState(defaults.baths);
  const [otherUnits, setOtherUnits] = useState(window.units);
  const [styles, setStyles] = useState([]);
  const [activeAmenities, setActiveAmenities] = useState([]);
  const [recommendedUnits, setRecommendedUnits] = useState([]);
  const [price, setPrice] = useState((maxDefault + minDefault) / 2);
  const [startDate, setStartDate] = useState(new Date());
  const [filtersOpen, setFiltersOpen] = useState(false);

  const scoreMultiplier = {
    "price" : 6, // will be out of a max of 6 already
    "beds"  : 9, // changed from 6 to 9 to allow for more bed points
    "baths" : 4,
    "style" : 20,
  };

  let maxScore = scoreMultiplier['price']
                 + scoreMultiplier['beds']
                 + scoreMultiplier['baths'];

  useEffect(() => {

    let newUnits = units;
    // filter out unit styles
    if(styles.length > 0) {
      maxScore += scoreMultiplier['style'];
    }

    console.log('maxScore:',maxScore);
    let highScore = 0;

    newUnits = newUnits.map(( unit, index ) => {
      let score = 0;

      // If beds requested is less than the unit, give a point
      if(beds == 0 && unit?.project?.fields?.studio) {
        score += scoreMultiplier['beds'];
      } else if (beds  == unit.beds) {
        score += scoreMultiplier['beds'];
      }

      // If baths requested is less than the unit, give a point
      if( baths <= unit.baths)  score += scoreMultiplier['baths'];

      // Score based on price
      let averageRent = (unit.minimumRent + unit.maximumRent) / 2;
      let priceScore  = scoreMultiplier['price'] - (distance(price, averageRent) / 200);
      if (priceScore > scoreMultiplier['price']) priceScore = scoreMultiplier['price'];
      score += priceScore;

      // Score all of the checked style matches
      score += styles.includes(unit.project?.fields.card_overrides.style) * scoreMultiplier['style'];

      console.log('includes styles ' + styles.includes(unit.project?.fields.card_overrides.style));
      // Score all of the checked amenity matches
      score += unit.amenities.reduce((a, c) => a + activeAmenities.includes(c), 0);

      // Score based on availability date (out of 7)
      let available = new Date(unit.availableDate);
      const now = new Date();

      if(available < now) available = now;

      const diffTime = available - startDate;
      let diffWeeks = diffTime / (1000 * 60 * 60 * 24 * 7);

      if(diffWeeks <= 2) {
        score += 3;
      } else if(diffWeeks <= 4) {
        score += 2;
      } else if(diffWeeks <= 6) {
        score += 1;
      }

      unit.rent  = averageRent;
      unit.score = score;
      unit.index = index;

      if(score > highScore) highScore = score;

      return unit;
    });

    // make the score a %
    newUnits = newUnits.map(( unit ) => {
      unit.percScore = average(unit.score, maxScore);

      return unit;
    });

    newUnits.sort((a, b) => {
      return b.score - a.score;
    });

    // Take top x and add to recommended list
    // console.clear();
    const newRecommendedUnits = newUnits.filter(( unit, n ) => {
      // console.log('Score: ' + unit.score + '/' + maxScore + ' (' + unit.percScore + '%)');
      return (n < numberOfFeatured && unit.percScore > 50);
    });

    setRecommendedUnits(newRecommendedUnits);

    // slice off the recommended units from the "other" units and only take top x
    newUnits = newUnits.splice(newRecommendedUnits.length, numberOfOthers);

    setOtherUnits(newUnits);

    console.log('going', newRecommendedUnits[0].score);
  }, [price, beds, baths, styles, activeAmenities, startDate]);

  const toggleFilters = () => {
    setFiltersOpen(!filtersOpen);
  }

  const unitsList = () => {
    if(recommendedUnits.length == 0) {
      return (
        <>
          <h2 className="rentals-section__headline">Sorry No Best Matches</h2>
          <p>Unfortunately, there is no current availability matching to your search criteria. Please consider revising your search or exploring all availability below.</p>
          <UnitsList units={ otherUnits } amenities={ activeAmenities } />
        </>

      );
    }
    return (
      <>
        <UnitsFeatured style="horizontal" units={ recommendedUnits } amenities={ activeAmenities } />
        <UnitsList units={ otherUnits } amenities={ activeAmenities } />
      </>
    )
  }

  return(
    <div className="rentals-section__container">
      <div className="rentals-section__col1">
        <div className="rentals-section__filters">
          <h2 className="rentals-section__title">Filters
            <a onClick={ () => toggleFilters() }>
              <svg xmlns="http://www.w3.org/2000/svg" className="rentals-section__title-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M0 19.75C0 19.1406 0.46875 18.625 1.125 18.625H3.89062C4.35938 17.125 5.8125 16 7.5 16C9.14062 16 10.5938 17.125 11.0625 18.625H22.875C23.4844 18.625 24 19.1406 24 19.75C24 20.4062 23.4844 20.875 22.875 20.875H11.0625C10.5938 22.4219 9.14062 23.5 7.5 23.5C5.8125 23.5 4.35938 22.4219 3.89062 20.875H1.125C0.46875 20.875 0 20.4062 0 19.75ZM6 19.75C6 20.5938 6.65625 21.25 7.5 21.25C8.29688 21.25 9 20.5938 9 19.75C9 18.9531 8.29688 18.25 7.5 18.25C6.65625 18.25 6 18.9531 6 19.75ZM15 12.25C15 13.0938 15.6562 13.75 16.5 13.75C17.2969 13.75 18 13.0938 18 12.25C18 11.4531 17.2969 10.75 16.5 10.75C15.6562 10.75 15 11.4531 15 12.25ZM16.5 8.5C18.1406 8.5 19.5938 9.625 20.0625 11.125H22.875C23.4844 11.125 24 11.6406 24 12.25C24 12.9062 23.4844 13.375 22.875 13.375H20.0625C19.5938 14.9219 18.1406 16 16.5 16C14.8125 16 13.3594 14.9219 12.8906 13.375H1.125C0.46875 13.375 0 12.9062 0 12.25C0 11.6406 0.46875 11.125 1.125 11.125H12.8906C13.3594 9.625 14.8125 8.5 16.5 8.5ZM9 6.25C9.79688 6.25 10.5 5.59375 10.5 4.75C10.5 3.95312 9.79688 3.25 9 3.25C8.15625 3.25 7.5 3.95312 7.5 4.75C7.5 5.59375 8.15625 6.25 9 6.25ZM12.5625 3.625H22.875C23.4844 3.625 24 4.14062 24 4.75C24 5.40625 23.4844 5.875 22.875 5.875H12.5625C12.0938 7.42188 10.6406 8.5 9 8.5C7.3125 8.5 5.85938 7.42188 5.39062 5.875H1.125C0.46875 5.875 0 5.40625 0 4.75C0 4.14062 0.46875 3.625 1.125 3.625H5.39062C5.85938 2.125 7.3125 1 9 1C10.6406 1 12.0938 2.125 12.5625 3.625Z" fill="white"/>
              </svg>
            </a>
          </h2>

          <div className={ filtersOpen ? 'rentals-section__filter-options rentals-section__filter-options--open' : 'rentals-section__filter-options' }>
            <div className="rentals-section__filter-options__content">
              <label className="rentals-section__label">Availability Date</label>
              <DatePicker
                  dateFormat="MMMM d, yyyy"
                  onChange={(date) => { setStartDate(date) }}
                  selected={startDate}
                  minDate={new Date() }
                  className="date-picker"
              />
              <label className="rentals-section__label">Bedrooms</label>
              <BedsFilter  value={ beds }  onChange={(beds) => { setBeds(beds) } }    />

              <label className="rentals-section__label">Bathrooms</label>
              <BathsFilter value={ baths } onChange={(baths) => { setBaths(baths) } } />

              <label className="rentals-section__label">Price</label>
              <PriceSlider min={ min } max={ max } value={ price } onChange={(price) => { setPrice(price)} }  />

              <label className="rentals-section__label">Product Type</label>
              <StylesFilter styles={ availableStyles } onChange={(styles) => { setStyles(styles) } } />

              <label className="rentals-section__label">Additional Features</label>
              <AmenitiesFilter amenities={ amenities } onChange={(amenities) => { setActiveAmenities(amenities) } } />
            </div>
          </div>
        </div>
      </div>
      <div className="rentals-section__col2">
        { unitsList() }
      </div>
    </div>
  );
}